import { Transform, Type } from 'class-transformer';
import {
  ArrayMaxSize,
  IsArray,
  IsBoolean,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsString,
  IsUUID,
  Matches,
  Min,
  ValidateNested,
} from 'class-validator';
import { OptionalStringTransform } from '../../validation/optional-string.transformer';

export class CompleteReceiptLotAllergenDto {
  @IsString()
  @IsNotEmpty()
  allergenId!: string;

  @IsString()
  @IsNotEmpty()
  category!: 'CONTAINS' | 'MAY_CONTAIN';
}

export class CompleteReceiptItemDto {
  @IsString()
  @IsNotEmpty()
  productName!: string;

  @IsNumber()
  @Transform(({ value }: { value: unknown }) =>
    value === '' || value === null ? undefined : Number(value),
  )
  quantity!: number;

  @IsString()
  @IsNotEmpty()
  unit!: string;

  @IsString()
  batchNumber!: string;

  @IsOptional()
  @IsString()
  expiryDate?: string;

  @IsOptional()
  @IsBoolean()
  hasNoExpiry?: boolean;

  @IsOptional()
  @IsBoolean()
  isDamaged?: boolean;

  @IsOptional()
  @IsBoolean()
  isTemperatureNonCompliant?: boolean;

  @IsOptional()
  @IsBoolean()
  isHygieneNonCompliant?: boolean;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  supplierName?: string;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => CompleteReceiptLotAllergenDto)
  allergens?: CompleteReceiptLotAllergenDto[];

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Transform(({ value }: { value: unknown }) =>
    value === '' || value === null ? undefined : Number(value),
  )
  unitPrice?: number;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Transform(({ value }: { value: unknown }) =>
    value === '' || value === null ? undefined : Number(value),
  )
  lineTotal?: number;

  @IsOptional()
  @IsString()
  @Matches(/^[A-Z]{3}$/)
  currency?: string;

  // Deliberately unconditional (not @ValidateIf'd on the other field): both
  // fields should always pass @IsUUID() when present. NOTE this class-validator
  // pipeline does NOT actually run for the completeReceipt route today —
  // ReceiptPayloadParser.parseReceiptData does a plain Object.assign on the
  // parsed JSON, no class-validator pass — so these decorators only protect
  // any future caller that does validate this DTO. The real, live-path
  // protection (XOR check + tenant-scoped existence/type check) lives in
  // DeliveryService (see validateReceiptItemStorageLocations /
  // assignStorageForCreatedLot), which runs before any DB write.
  @IsOptional()
  @IsUUID()
  storageEquipmentId?: string;

  @IsOptional()
  @IsUUID()
  storageCleaningAreaId?: string;
}

export class CompleteReceiptDataDto {
  @IsString()
  @IsNotEmpty()
  supplierId!: string;

  @IsString()
  @IsNotEmpty()
  documentNumber!: string;

  @IsString()
  @IsNotEmpty()
  date!: string;

  @IsOptional()
  @IsNumber()
  @Transform(({ value }: { value: unknown }) =>
    value === '' || value === null ? undefined : Number(value),
  )
  temperature?: number;

  @IsOptional()
  @IsBoolean()
  vehicleHygiene?: boolean;

  @IsOptional()
  @IsBoolean()
  packagingIntact?: boolean;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  notes?: string;

  @IsOptional()
  @IsString()
  @OptionalStringTransform()
  supplierName?: string;

  @IsOptional()
  @IsNumber()
  @Min(0)
  @Transform(({ value }: { value: unknown }) =>
    value === '' || value === null ? undefined : Number(value),
  )
  totalAmount?: number;

  @IsOptional()
  @IsString()
  @Matches(/^[A-Z]{3}$/)
  currency?: string;

  @IsArray()
  @ArrayMaxSize(200)
  @ValidateNested({ each: true })
  @Type(() => CompleteReceiptItemDto)
  items!: CompleteReceiptItemDto[];
}
